import {Injectable} from '@angular/core';
import {NetworkStatusService} from './network-status.service';

export type MediaOfflineType = 'image' | 'video' | 'playlist' | 'unknown';

export interface MediaPrewarmOptions {
  /** Replaces the previous background window owned by this consumer. */
  scope?: string;
  /** Full HLS caching is reserved for media the user actually starts playing. */
  cachePlaylistChildren?: boolean;
  /** User intent may move one shallow task ahead of background viewport work. */
  priority?: boolean;
  /**
   * Keeps a tiny HLS startup path on disk: the selected rendition manifest,
   * optional init/key files and this many media fragments. It never creates a
   * media element or downloads the complete stream.
   */
  startupSegmentCount?: number;
  /**
   * Keep a bounded viewport prewarm available in Cache Storage after its UI
   * owner leaves the screen. Runtime decoders are still always released by
   * their owner; normal cache limits and LRU eviction control disk usage.
   */
  retainCacheOnScopeRelease?: boolean;
}

interface MediaOfflineEntry {
  url: string;
  type: MediaOfflineType;
  size: number;
  cachedAt: number;
  lastAccessedAt: number;
  ttlMs: number;
  source?: 'prewarm' | 'played';
  /** A viewport startup path may outlive its component, but never its cache budget. */
  retainCacheOnScopeRelease?: boolean;
}

interface MediaOfflineIndex {
  version: number;
  entries: Record<string, MediaOfflineEntry>;
}

@Injectable({providedIn: 'root'})
export class MediaOfflineService {
  private readonly cacheName = 'wtg-media-offline-v1';
  private readonly indexStorageKey = 'wtg.media.offline.index.v1';
  private readonly indexVersion = 1;
  private readonly imageMaxBytes = 300 * 1024 * 1024;
  private readonly videoMaxBytes = 2 * 1024 * 1024 * 1024;
  private readonly imageTtlMs = 14 * 24 * 60 * 60 * 1000;
  private readonly videoTtlMs = 30 * 24 * 60 * 60 * 1000;
  private readonly playlistTtlMs = 7 * 24 * 60 * 60 * 1000;

  private initialized = false;
  private index: MediaOfflineIndex = {version: this.indexVersion, entries: {}};
  private readonly inFlight = new Map<string, Promise<void>>();
  private readonly materializedBlobUrls = new Map<string, string>();
  private readonly materializedBlobUrlAccess = new Map<string, number>();
  private readonly prewarmScopes = new Map<string, Set<string>>();
  /** Descendants created by a temporary HLS startup prewarm, keyed by root playlist. */
  private readonly prewarmPlaylistChildren = new Map<string, Set<string>>();
  private readonly playedRoots = new Map<string, number>();
  private readonly playlistChildren = new Map<string, Set<string>>();
  private readonly maxPlayedRoots = 20;
  private readonly maxMaterializedBlobUrls = 20;
  private readonly backgroundQueue: Array<{
    url: string;
    typeHint: MediaOfflineType;
    cachePlaylistChildren: boolean;
    startupSegmentCount: number;
    retainCacheOnScopeRelease: boolean;
    scope?: string;
    priority: boolean;
  }> = [];
  private readonly queuedUrls = new Set<string>();
  /** A tap can deepen a viewport task already being fetched without restarting it. */
  private readonly inFlightStartupPromotions = new Map<string, number>();
  /** A scope replacement may promote an in-flight task from transient to retained disk cache. */
  private readonly inFlightRetentionPromotions = new Set<string>();
  private backgroundTasksInFlight = 0;
  // Viewport prewarm is disk-only: allowing a few parallel fetches prepares
  // every currently tappable tile without allocating media elements or HLS.
  private readonly maxBackgroundTasks = 4;

  constructor(private readonly networkStatus: NetworkStatusService) {}

  async init(): Promise<void> {
    if (this.initialized || !this.isBrowser()) {
      return;
    }
    this.initialized = true;
    this.index = this.readIndex();
    await this.cleanupExpired();
  }

  async resolveUrl(url: string, typeHint: MediaOfflineType = 'unknown'): Promise<string> {
    if (!url || !this.isBrowser()) {
      return url;
    }
    if (!this.networkStatus.isOffline()) {
      // The browser already loads the requested asset online. Background
      // caching is intentionally started only by a bounded prewarm scope or
      // after actual video playback; doing it for every pipe evaluation makes
      // an infinite timeline retain the entire browsing history.
      //
      // Do not await Cache Storage index hydration here. HLS startup and a
      // grid-to-modal handoff call this method on the critical path, while the
      // service worker can satisfy the same URL from its HTTP/Cache Storage
      // cache without an object URL. Waiting for `init()` made a fast tap look
      // cold even when all startup fragments had already been persisted.
      return url;
    }

    await this.init();

    const hit = await this.hasCached(url);
    if (!hit) {
      return url;
    }

    this.touch(url);
    if (typeHint === 'playlist') {
      const playlistUrl = await this.tryCreateOfflinePlaylistUrl(url, new Set<string>());
      return playlistUrl || url;
    }
    const objectUrl = await this.tryCreateObjectUrl(url);
    return objectUrl || url;
  }

  prewarmUrls(
    urls: string[],
    typeHint: MediaOfflineType = 'unknown',
    limit: number = 6,
    options: MediaPrewarmOptions = {}
  ): void {
    if (!urls?.length || !this.isBrowser()) {
      return;
    }
    const unique = Array.from(new Set(urls.filter(Boolean))).slice(0, Math.max(0, limit));
    if (options.scope) {
      this.replacePrewarmScope(options.scope, unique);
    }
    // Priority tasks are pushed at the front of the shared queue. Iterate in
    // reverse so their user-visible viewport order is preserved instead of
    // warming the last tile before the first one.
    const ordered = options.priority ? [...unique].reverse() : unique;
    ordered.forEach((url) => this.enqueueBackgroundPrefetch(
      url,
      typeHint,
      !!options.cachePlaylistChildren,
      Math.max(0, Math.floor(options.startupSegmentCount || 0)),
      !!options.retainCacheOnScopeRelease,
      options.scope,
      !!options.priority
    ));
  }

  /** Persists a video playlist and its segments only after actual playback begins. */
  cacheForOffline(url: string, typeHint: MediaOfflineType = 'unknown'): void {
    this.touchPlayedRoot(url);
    const activeTask = this.inFlight.get(url);
    if (activeTask) {
      void activeTask.then(() => this.prefetch(url, typeHint, true, 'played'));
      return;
    }
    void this.prefetch(url, typeHint, true, 'played');
  }

  clearPrewarmScope(scope: string): void {
    const previousUrls = this.prewarmScopes.get(scope);
    if (!previousUrls) {
      return;
    }
    this.prewarmScopes.delete(scope);
    this.removeQueuedScopeTasks(scope);
    previousUrls.forEach((url) => this.releasePrewarmEntryIfUnused(url));
  }

  clearBlobUrls(): void {
    this.materializedBlobUrls.forEach((blobUrl) => URL.revokeObjectURL(blobUrl));
    this.materializedBlobUrls.clear();
    this.materializedBlobUrlAccess.clear();
  }

  /** Development-only friendly counters; no cache content is exposed. */
  getDiagnostics(): {
    cachedEntries: number;
    prewarmScopes: number;
    queuedTasks: number;
    inFlightTasks: number;
    playedRoots: number;
    materializedBlobUrls: number;
  } {
    return {
      cachedEntries: Object.keys(this.index.entries).length,
      prewarmScopes: this.prewarmScopes.size,
      queuedTasks: this.backgroundQueue.length,
      inFlightTasks: this.inFlight.size,
      playedRoots: this.playedRoots.size,
      materializedBlobUrls: this.materializedBlobUrls.size,
    };
  }

  private enqueueBackgroundPrefetch(
    url: string,
    typeHint: MediaOfflineType,
    cachePlaylistChildren: boolean,
    startupSegmentCount: number,
    retainCacheOnScopeRelease: boolean,
    scope?: string,
    priority: boolean = false
  ): void {
    if (!url) {
      return;
    }
    const inFlightTask = this.inFlight.get(url);
    if (inFlightTask) {
      const promotedSegmentCount = Math.max(this.inFlightStartupPromotions.get(url) || 0, startupSegmentCount);
      if (retainCacheOnScopeRelease) {
        this.inFlightRetentionPromotions.add(url);
      }
      if (promotedSegmentCount > 0 || retainCacheOnScopeRelease) {
        this.inFlightStartupPromotions.set(url, promotedSegmentCount);
        void inFlightTask.finally(() => {
          const requestedSegmentCount = this.inFlightStartupPromotions.get(url) || 0;
          const requestedRetention = this.inFlightRetentionPromotions.delete(url);
          this.inFlightStartupPromotions.delete(url);
          if ((!requestedSegmentCount && !requestedRetention) || (scope && !this.prewarmScopes.get(scope)?.has(url) && !requestedRetention)) {
            return;
          }
          void this.prefetch(url, typeHint, cachePlaylistChildren, 'prewarm', requestedSegmentCount, url, requestedRetention);
        });
      }
      return;
    }
    const queuedIndex = this.backgroundQueue.findIndex((task) => task.url === url);
    if (queuedIndex >= 0) {
      const queuedTask = this.backgroundQueue.splice(queuedIndex, 1)[0];
      queuedTask.startupSegmentCount = Math.max(queuedTask.startupSegmentCount, startupSegmentCount);
      queuedTask.retainCacheOnScopeRelease = queuedTask.retainCacheOnScopeRelease || retainCacheOnScopeRelease;
      queuedTask.priority = queuedTask.priority || priority;
      if (scope) {
        queuedTask.scope = scope;
      }
      if (priority) {
        this.backgroundQueue.unshift(queuedTask);
      } else {
        this.backgroundQueue.splice(queuedIndex, 0, queuedTask);
      }
      return;
    }
    this.queuedUrls.add(url);
    const task = {url, typeHint, cachePlaylistChildren, startupSegmentCount, retainCacheOnScopeRelease, scope, priority};
    if (priority) {
      this.backgroundQueue.unshift(task);
    } else {
      this.backgroundQueue.push(task);
    }
    this.drainBackgroundQueue();
  }

  private drainBackgroundQueue(): void {
    while (this.backgroundTasksInFlight < this.maxBackgroundTasks && this.backgroundQueue.length) {
      const task = this.backgroundQueue.shift()!;
      this.queuedUrls.delete(task.url);
      if (task.scope && !this.prewarmScopes.get(task.scope)?.has(task.url)) {
        continue;
      }
      this.backgroundTasksInFlight++;
      void this.prefetch(
        task.url,
        task.typeHint,
        task.cachePlaylistChildren,
        'prewarm',
        task.startupSegmentCount,
        task.url,
        task.retainCacheOnScopeRelease
      )
        .finally(() => {
          this.backgroundTasksInFlight--;
          if (task.scope && !this.prewarmScopes.get(task.scope)?.has(task.url)) {
            this.releasePrewarmEntryIfUnused(task.url);
          }
          this.drainBackgroundQueue();
        });
    }
  }

  private replacePrewarmScope(scope: string, urls: string[]): void {
    const nextUrls = new Set(urls);
    const previousUrls = this.prewarmScopes.get(scope) || new Set<string>();
    this.prewarmScopes.set(scope, nextUrls);
    this.removeQueuedScopeTasks(scope);
    previousUrls.forEach((url) => {
      if (!nextUrls.has(url)) {
        this.releasePrewarmEntryIfUnused(url);
      }
    });
  }

  private removeQueuedScopeTasks(scope: string): void {
    for (let index = this.backgroundQueue.length - 1; index >= 0; index--) {
      if (this.backgroundQueue[index].scope === scope) {
        this.queuedUrls.delete(this.backgroundQueue[index].url);
        this.backgroundQueue.splice(index, 1);
      }
    }
  }

  private releasePrewarmEntryIfUnused(url: string): void {
    const isStillReferenced = Array.from(this.prewarmScopes.values()).some((urls) => urls.has(url));
    if (!isStillReferenced && this.index.entries[url]?.source === 'prewarm') {
      if (this.index.entries[url].retainCacheOnScopeRelease) {
        // The scope no longer owns any RAM resources. Keep only the disk cache
        // and discard this temporary graph so scrolling cannot retain URLs.
        this.prewarmPlaylistChildren.delete(url);
        return;
      }
      void this.removeEntry(url);
      void this.releasePrewarmPlaylistChildren(url, new Set<string>());
    }
  }

  private async releasePrewarmPlaylistChildren(rootUrl: string, visited: Set<string>): Promise<void> {
    if (visited.has(rootUrl)) {
      return;
    }
    visited.add(rootUrl);
    const children = Array.from(this.prewarmPlaylistChildren.get(rootUrl) || []);
    this.prewarmPlaylistChildren.delete(rootUrl);

    for (const childUrl of children) {
      const isStillWarm = Array.from(this.prewarmPlaylistChildren.values()).some((urls) => urls.has(childUrl));
      const isChildRootReferenced = Array.from(this.prewarmScopes.values()).some((urls) => urls.has(childUrl));
      if (isStillWarm || isChildRootReferenced || this.index.entries[childUrl]?.source !== 'prewarm') {
        continue;
      }
      await this.removeEntry(childUrl);
      await this.releasePrewarmPlaylistChildren(childUrl, visited);
    }
  }

  private touchPlayedRoot(url: string): void {
    this.playedRoots.delete(url);
    this.playedRoots.set(url, Date.now());
    while (this.playedRoots.size > this.maxPlayedRoots) {
      const oldestUrl = this.playedRoots.keys().next().value as string | undefined;
      if (!oldestUrl) {
        return;
      }
      this.playedRoots.delete(oldestUrl);
      void this.releasePlayedRoot(oldestUrl, new Set<string>());
    }
  }

  private async releasePlayedRoot(url: string, visited: Set<string>): Promise<void> {
    if (visited.has(url)) {
      return;
    }
    visited.add(url);
    const children = Array.from(this.playlistChildren.get(url) || []);
    this.playlistChildren.delete(url);
    await Promise.all(children.map((childUrl) => this.releasePlayedRoot(childUrl, visited)));
    if (!Array.from(this.playlistChildren.values()).some((urls) => urls.has(url))) {
      await this.removeEntry(url);
    }
  }

  private async prefetch(
    url: string,
    typeHint: MediaOfflineType,
    cachePlaylistChildren: boolean,
    source: 'prewarm' | 'played',
    startupSegmentCount: number = 0,
    prewarmRoot?: string,
    retainCacheOnScopeRelease: boolean = false
  ): Promise<void> {
    if (!url || this.inFlight.has(url)) {
      return this.inFlight.get(url);
    }

    const task = (async () => {
      try {
        await this.init();
        if (await this.hasCached(url)) {
          this.touch(url);
          if (source === 'prewarm' && retainCacheOnScopeRelease && this.index.entries[url]) {
            this.index.entries[url].retainCacheOnScopeRelease = true;
            this.persistIndex();
          }
          if (source === 'played' && this.index.entries[url]) {
            this.index.entries[url].source = 'played';
            this.index.entries[url].retainCacheOnScopeRelease = false;
            this.persistIndex();
            this.prewarmPlaylistChildren.delete(url);
          }
          if (cachePlaylistChildren && typeHint === 'playlist') {
            const cache = await caches.open(this.cacheName);
            const response = await cache.match(url);
            if (response) {
              if (this.index.entries[url]) {
                this.index.entries[url].source = 'played';
              }
              this.persistIndex();
              await this.prefetchHlsChildren(url, response.clone());
            }
          } else if (startupSegmentCount > 0 && typeHint === 'playlist') {
            const cache = await caches.open(this.cacheName);
            const response = await cache.match(url);
            if (response) {
              await this.prefetchHlsStartup(
                url,
                response.clone(),
                startupSegmentCount,
                prewarmRoot || url,
                retainCacheOnScopeRelease
              );
            }
          }
          return;
        }

        const response = await this.fetchForCache(url);
        if (!response) {
          return;
        }

        const type = this.detectType(url, response, typeHint);
        const ttlMs = this.getTtl(type);
        const size = await this.estimateResponseSize(response);
        const cache = await caches.open(this.cacheName);
        await cache.put(url, response.clone());

        this.index.entries[url] = {
          url,
          type,
          size,
          cachedAt: Date.now(),
          lastAccessedAt: Date.now(),
          ttlMs,
          source,
          retainCacheOnScopeRelease: source === 'prewarm' && retainCacheOnScopeRelease,
        };
        if (source === 'played') {
          this.prewarmPlaylistChildren.delete(url);
        }
        this.persistIndex();

        await this.evictIfNeeded(type);

        if (type === 'playlist' && cachePlaylistChildren) {
          await this.prefetchHlsChildren(url, response.clone());
        } else if (type === 'playlist' && startupSegmentCount > 0) {
          await this.prefetchHlsStartup(url, response.clone(), startupSegmentCount, prewarmRoot || url, retainCacheOnScopeRelease);
        }
      } catch {
        // Ignore media prefetch errors.
      } finally {
        this.inFlight.delete(url);
        if (source === 'prewarm' && prewarmRoot === url && retainCacheOnScopeRelease) {
          // Descendant URLs are now ordinary Cache Storage entries. Do not
          // retain their startup graph in RAM after the scoped warm-up ends.
          this.prewarmPlaylistChildren.delete(url);
        }
      }
    })();

    this.inFlight.set(url, task);
    return task;
  }

  private async prefetchHlsChildren(playlistUrl: string, playlistResponse: Response): Promise<void> {
    try {
      if (playlistResponse.type === 'opaque') {
        return;
      }
      const body = await playlistResponse.text();
      const links = body
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line && !line.startsWith('#'))
        .map((line) => {
          try {
            return new URL(line, playlistUrl).toString();
          } catch {
            return null;
          }
        })
        .filter((value): value is string => !!value);

      for (const childUrl of links) {
        const children = this.playlistChildren.get(playlistUrl) || new Set<string>();
        children.add(childUrl);
        this.playlistChildren.set(playlistUrl, children);
        const lower = childUrl.toLowerCase();
        const type: MediaOfflineType = lower.includes('.m3u8') ? 'playlist' : 'video';
        await this.prefetch(childUrl, type, true, 'played');
      }
    } catch {
      // Ignore parse failures.
    }
  }

  /**
   * Prepares only the first usable HLS path. A master playlist may contain
   * several renditions; the player starts from Hls level 0, so warming that
   * exact rendition avoids multiplying every visible tile by every bitrate
   * and prevents a cache hit being discarded for a different initial level.
   */
  private async prefetchHlsStartup(
    playlistUrl: string,
    playlistResponse: Response,
    segmentCount: number,
    prewarmRoot: string,
    retainCacheOnScopeRelease: boolean
  ): Promise<void> {
    try {
      if (playlistResponse.type === 'opaque') {
        return;
      }
      const lines = (await playlistResponse.text()).split('\n').map((line) => line.trim());
      const variantUrl = this.getInitialHlsVariantUrl(lines, playlistUrl);
      if (variantUrl) {
        await this.prefetchHlsStartupChild(prewarmRoot, variantUrl, 'playlist', segmentCount, retainCacheOnScopeRelease);
        return;
      }

      const initializationUrls = this.getHlsTagUrls(lines, playlistUrl, '#EXT-X-MAP')
        .concat(this.getHlsTagUrls(lines, playlistUrl, '#EXT-X-KEY'));
      for (const initializationUrl of initializationUrls) {
        await this.prefetchHlsStartupChild(prewarmRoot, initializationUrl, 'video', 0, retainCacheOnScopeRelease);
      }

      const segmentUrls = lines
        .filter((line) => line && !line.startsWith('#'))
        .map((line) => this.resolveHlsUrl(line, playlistUrl))
        .filter((url): url is string => !!url)
        .slice(0, segmentCount);
      for (const segmentUrl of segmentUrls) {
        await this.prefetchHlsStartupChild(prewarmRoot, segmentUrl, 'video', 0, retainCacheOnScopeRelease);
      }
    } catch {
      // Startup warming is opportunistic; active playback always keeps its
      // normal HLS network fallback.
    }
  }

  private async prefetchHlsStartupChild(
    prewarmRoot: string,
    childUrl: string,
    typeHint: MediaOfflineType,
    segmentCount: number,
    retainCacheOnScopeRelease: boolean
  ): Promise<void> {
    const children = this.prewarmPlaylistChildren.get(prewarmRoot) || new Set<string>();
    children.add(childUrl);
    this.prewarmPlaylistChildren.set(prewarmRoot, children);
    await this.prefetch(childUrl, typeHint, false, 'prewarm', segmentCount, prewarmRoot, retainCacheOnScopeRelease);
  }

  /** Returns the first declared master-playlist rendition, matching Hls level 0. */
  private getInitialHlsVariantUrl(lines: string[], playlistUrl: string): string | null {
    for (let index = 0; index < lines.length; index += 1) {
      if (!lines[index].startsWith('#EXT-X-STREAM-INF')) {
        continue;
      }
      for (let candidateIndex = index + 1; candidateIndex < lines.length; candidateIndex += 1) {
        const candidate = lines[candidateIndex];
        if (!candidate) {
          continue;
        }
        if (candidate.startsWith('#')) {
          break;
        }
        const url = this.resolveHlsUrl(candidate, playlistUrl);
        if (url) {
          return url;
        }
        break;
      }
    }
    return null;
  }

  private getHlsTagUrls(lines: string[], playlistUrl: string, tag: string): string[] {
    return lines
      .filter((line) => line.startsWith(tag))
      .map((line) => line.match(/URI="([^"]+)"/)?.[1] || null)
      .map((uri) => uri ? this.resolveHlsUrl(uri, playlistUrl) : null)
      .filter((url): url is string => !!url);
  }

  private resolveHlsUrl(value: string, baseUrl: string): string | null {
    try {
      return new URL(value, baseUrl).toString();
    } catch {
      return null;
    }
  }

  private async fetchForCache(url: string): Promise<Response | null> {
    try {
      const response = await fetch(url, {
        method: 'GET',
        // Uploaded HLS assets use immutable versioned URLs. `force-cache`
        // avoids a 70–135 ms conditional 304 round trip before a viewport
        // prewarm can populate Cache Storage.
        cache: 'force-cache',
        credentials: 'omit'
      });
      if (!response.ok && response.type !== 'opaque') {
        return null;
      }
      return response;
    } catch {
      return null;
    }
  }

  private async hasCached(url: string): Promise<boolean> {
    if (!('caches' in window)) {
      return false;
    }
    const cache = await caches.open(this.cacheName);
    const match = await cache.match(url);
    return !!match;
  }

  private async tryCreateObjectUrl(url: string): Promise<string | null> {
    if (this.materializedBlobUrls.has(url)) {
      this.touchMaterializedBlobUrl(url);
      return this.materializedBlobUrls.get(url) || null;
    }
    try {
      const cache = await caches.open(this.cacheName);
      const response = await cache.match(url);
      if (!response || response.type === 'opaque') {
        return null;
      }
      const blob = await response.blob();
      if (!blob || !blob.size) {
        return null;
      }
      const blobUrl = URL.createObjectURL(blob);
      this.registerMaterializedBlobUrl(url, blobUrl);
      return blobUrl;
    } catch {
      return null;
    }
  }

  private async tryCreateOfflinePlaylistUrl(url: string, visited: Set<string>): Promise<string | null> {
    const cacheKey = `playlist:${url}`;
    if (this.materializedBlobUrls.has(cacheKey)) {
      this.touchMaterializedBlobUrl(cacheKey);
      return this.materializedBlobUrls.get(cacheKey) || null;
    }
    if (visited.has(url)) {
      return null;
    }
    visited.add(url);
    try {
      const cache = await caches.open(this.cacheName);
      const response = await cache.match(url);
      if (!response || response.type === 'opaque') {
        return null;
      }
      const body = await response.text();
      const rewrittenLines: string[] = [];

      for (const line of body.split('\n')) {
        const trimmed = line.trim();
        if (!trimmed || trimmed.startsWith('#')) {
          rewrittenLines.push(line);
          continue;
        }

        let childUrl: string;
        try {
          childUrl = new URL(trimmed, url).toString();
        } catch {
          rewrittenLines.push(line);
          continue;
        }

        const childType: MediaOfflineType = childUrl.toLowerCase().includes('.m3u8') ? 'playlist' : 'video';
        const offlineChild = childType === 'playlist'
          ? await this.tryCreateOfflinePlaylistUrl(childUrl, visited) || childUrl
          : await this.tryCreateObjectUrl(childUrl) || childUrl;

        rewrittenLines.push(offlineChild);
      }

      const blob = new Blob([rewrittenLines.join('\n')], {type: 'application/vnd.apple.mpegurl'});
      const blobUrl = URL.createObjectURL(blob);
      this.registerMaterializedBlobUrl(cacheKey, blobUrl);
      return blobUrl;
    } catch {
      return null;
    } finally {
      visited.delete(url);
    }
  }

  private touch(url: string): void {
    const entry = this.index.entries[url];
    if (!entry) {
      return;
    }
    entry.lastAccessedAt = Date.now();
    this.persistIndex();
  }

  private registerMaterializedBlobUrl(key: string, blobUrl: string): void {
    const previousBlobUrl = this.materializedBlobUrls.get(key);
    if (previousBlobUrl && previousBlobUrl !== blobUrl) {
      URL.revokeObjectURL(previousBlobUrl);
    }
    this.materializedBlobUrls.set(key, blobUrl);
    this.touchMaterializedBlobUrl(key);

    while (this.materializedBlobUrls.size > this.maxMaterializedBlobUrls) {
      const oldestKey = this.materializedBlobUrlAccess.keys().next().value as string | undefined;
      if (!oldestKey) {
        return;
      }
      const oldestBlobUrl = this.materializedBlobUrls.get(oldestKey);
      if (oldestBlobUrl) {
        URL.revokeObjectURL(oldestBlobUrl);
      }
      this.materializedBlobUrls.delete(oldestKey);
      this.materializedBlobUrlAccess.delete(oldestKey);
    }
  }

  private touchMaterializedBlobUrl(key: string): void {
    this.materializedBlobUrlAccess.delete(key);
    this.materializedBlobUrlAccess.set(key, Date.now());
  }

  private async evictIfNeeded(type: MediaOfflineType): Promise<void> {
    const poolTypes = this.toPoolTypes(type);
    for (const poolType of poolTypes) {
      const maxBytes = poolType === 'image' ? this.imageMaxBytes : this.videoMaxBytes;
      const poolEntries = this.getEntriesByPool(poolType);
      let poolSize = poolEntries.reduce((acc, entry) => acc + (entry.size || 0), 0);
      if (poolSize <= maxBytes) {
        continue;
      }

      const sorted = [...poolEntries].sort((a, b) => a.lastAccessedAt - b.lastAccessedAt);
      for (const entry of sorted) {
        await this.removeEntry(entry.url);
        poolSize -= entry.size || 0;
        if (poolSize <= maxBytes) {
          break;
        }
      }
    }
  }

  private async cleanupExpired(): Promise<void> {
    const now = Date.now();
    const candidates = Object.values(this.index.entries).filter((entry) => now - entry.cachedAt > entry.ttlMs);
    for (const entry of candidates) {
      await this.removeEntry(entry.url);
    }
  }

  private async removeEntry(url: string): Promise<void> {
    try {
      const cache = await caches.open(this.cacheName);
      await cache.delete(url);
    } catch {
      // Ignore cache delete failures.
    }

    delete this.index.entries[url];
    this.persistIndex();

    const blobUrl = this.materializedBlobUrls.get(url);
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      this.materializedBlobUrls.delete(url);
    }
    const playlistBlobUrl = this.materializedBlobUrls.get(`playlist:${url}`);
    if (playlistBlobUrl) {
      URL.revokeObjectURL(playlistBlobUrl);
      this.materializedBlobUrls.delete(`playlist:${url}`);
    }
  }

  private getEntriesByPool(type: 'image' | 'video'): MediaOfflineEntry[] {
    return Object.values(this.index.entries).filter((entry) => {
      if (type === 'image') {
        return entry.type === 'image';
      }
      return entry.type === 'video' || entry.type === 'playlist';
    });
  }

  private toPoolTypes(type: MediaOfflineType): Array<'image' | 'video'> {
    if (type === 'image') {
      return ['image'];
    }
    return ['video'];
  }

  private detectType(url: string, response: Response, typeHint: MediaOfflineType): MediaOfflineType {
    if (typeHint !== 'unknown') {
      return typeHint;
    }

    const lower = url.toLowerCase();
    if (lower.includes('.m3u8')) {
      return 'playlist';
    }
    if (/(\.ts|\.m4s|\.mp4|\.webm)(\?|$)/.test(lower)) {
      return 'video';
    }
    if (/(\.jpg|\.jpeg|\.png|\.webp|\.gif|\.avif)(\?|$)/.test(lower)) {
      return 'image';
    }

    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/vnd.apple.mpegurl')) {
      return 'playlist';
    }
    if (contentType.startsWith('video/')) {
      return 'video';
    }
    if (contentType.startsWith('image/')) {
      return 'image';
    }

    return 'unknown';
  }

  private getTtl(type: MediaOfflineType): number {
    switch (type) {
      case 'video':
        return this.videoTtlMs;
      case 'playlist':
        return this.playlistTtlMs;
      case 'image':
      default:
        return this.imageTtlMs;
    }
  }

  private async estimateResponseSize(response: Response): Promise<number> {
    const headerSize = Number(response.headers.get('content-length') || 0);
    if (headerSize > 0) {
      return headerSize;
    }

    if (response.type === 'opaque') {
      return 0;
    }

    try {
      const buffer = await response.clone().arrayBuffer();
      return buffer.byteLength;
    } catch {
      return 0;
    }
  }

  private persistIndex(): void {
    try {
      localStorage.setItem(this.indexStorageKey, JSON.stringify(this.index));
    } catch {
      // Ignore storage quota errors.
    }
  }

  private readIndex(): MediaOfflineIndex {
    try {
      const raw = localStorage.getItem(this.indexStorageKey);
      if (!raw) {
        return {version: this.indexVersion, entries: {}};
      }
      const parsed = JSON.parse(raw) as MediaOfflineIndex;
      if (!parsed?.entries || parsed.version !== this.indexVersion) {
        return {version: this.indexVersion, entries: {}};
      }
      return parsed;
    } catch {
      return {version: this.indexVersion, entries: {}};
    }
  }

  private isBrowser(): boolean {
    return typeof window !== 'undefined' && typeof caches !== 'undefined';
  }
}
